import * as comlink from "comlink"
import { uuidv7 } from "uuidv7"

import type { CopyFinishedStatusDatagram } from "~/models/polycache/CopyFinishedStatusDatagram"

import { PropertyId, PropertySchema, PropertyStatus } from "../entity"
import { StaticFile } from "../file/StaticFile"
import { createFileCopyRequestDatagram } from "../handlers/file/copy"

// TODO: maybe want a web and a native version of this class?
export type CopyJobStatus = "preparing" | "copying" | "failed" | "complete"
export type CopyJobFileStatus = "prepared" | "complete" | "failed"

export interface CopyJobState {
  type: "copy"
  transferId: string
  status: CopyJobStatus
  title?: string // the folder name to upload, or something else, etc.
  timestamp: number
  folderId: string
  sources: Record<string, StaticFile>
  files: Record<string, {
    fileId: string
    totalBytes: number
    completedBytes: number
    status: CopyJobFileStatus
    name?: string
    isFolder?: boolean
  }>
}

/**
 * The root copy job class, which defines any job, whether it is on this tab/window,
 * or an external source of truth. Subclasses of this class include the BrowserCopyJob,
 * which is a browser-based copy job, as well as a DesktopCopyJob, which is an copy
 * job started on desktop. The root job class is created when we receive a notification
 * of another tab's job.
 */
export class CopyJob {
  state: Ref<CopyJobState>

  constructor(state: Ref<CopyJobState>) {
    this.state = state
  }

  started(start: CopyStartedStatusDatagram) {
    const { transferId, folderId } = start
    if (transferId !== this.state.value.transferId || folderId !== this.state.value.folderId)
      return console.error("This update doesn't match", start)
    this.state.value.status = "preparing"
  }

  static addFromStartDatagram(start: CopyStartedStatusDatagram) {
    const { state } = useJobs()
    const job: CopyJobState = {
      type: "copy",
      transferId: start.transferId,
      status: "preparing",
      timestamp: Date.now(),
      folderId: start.folderId,
      sources: {},
      files: {},
    }
    state.value.jobs[job.transferId] = job
    return new CopyJob(toRef(state.value.jobs, job.transferId) as Ref<CopyJobState>)
  }

  fileUpdated(update: CopyFileProgressStatusDatagram) {
    const { transferId, fileId, totalBytes, completedBytes } = update
    if (transferId !== this.state.value.transferId)
      return console.error("This update doesn't match", update)

    this.state.value.status = "copying"

    if (this.state.value.files[fileId] === undefined)
      this.state.value.files[fileId] = {} as any

    this.state.value.files[fileId].fileId = update.fileId
    this.state.value.files[fileId].completedBytes = Number(completedBytes)
    this.state.value.files[fileId].totalBytes = Number(totalBytes)
    if (this.state.value.files[fileId].totalBytes === this.state.value.files[fileId].completedBytes)
      this.state.value.files[fileId].status = "complete"
    else
      this.state.value.files[fileId].status = "failed"
  }

  fileFailed(update: CopyFileFailedStatusDatagram) {
    if (update.transferId !== this.state.value.transferId)
      return console.error("This update doesn't match", update)

    if (this.state.value.files[update.file.id] === undefined) {
      this.state.value.files[update.file.id] = {
        fileId: update.file.id,
        totalBytes: 0,
        completedBytes: 0,
        status: "failed",
      }
    }
    else {
      this.state.value.files[update.file.id].completedBytes = this.state.value.files[update.file.id].totalBytes
      this.state.value.files[update.file.id].status = "failed"
    }
  }

  finished(finished: CopyFinishedStatusDatagram) {
    if (finished.transferId !== this.state.value.transferId || finished.folderId !== this.state.value.folderId)
      return console.error("This update doesn't match", finished)

    if (!finished.approxTotalBytes || finished.approxTotalBytes !== finished.failedBytes)
      this.state.value.status = "complete"
    else
      this.state.value.status = "failed"
  }

  #bytesByType = (type: CopyJobFileStatus) => computed(() => Object
    .values(this.state.value.files)
    .filter(f => f.status === type)
    .map(f => f.totalBytes)
    .reduce((a, b) => a + b, 0))

  totalFiles = computed(() => Object.values(this.state.value.files).length)
  totalBytes = computed(() => Object.values(this.state.value.files).map(f => f.totalBytes).reduce((a, b) => a + b, 0))
  /** This includes all uploaded, completed, or failed bytes */
  finishedBytes = computed(() => Object.values(this.state.value.files).map(f => f.completedBytes).reduce((a, b) => a + b, 0))

  completedBytes = this.#bytesByType("complete")
  failedBytes = this.#bytesByType("failed")

  progressDetails = () => {
    return {
      finishedBytes: this.finishedBytes.value,
      successBytes: this.completedBytes.value,
      failedBytes: this.failedBytes.value,
      totalBytes: this.totalBytes.value,
    }
  }
}

/**
 * A browser-based copy job that can parse the contents of a `DataTransfer` object and
 * construct a job based out of that.
 */
export class BrowserCopyJob extends CopyJob {
  async startCopy() {
    const { state: myself } = useSelf()
    const { cacheWorker } = useCache()

    if (!myself.value.user)
      return console.error("Cannot upload while logged out")

    const requestId = generateWorkerRequestId()
    const timestamp = uuidv7()
    const requests: FileCopyRequestDatagram[] = []
    for (const sourceFile of Object.values(this.state.value.sources)) {
      const preHash = StaticFile.clientPreHashId()
      const originalFileId = sourceFile.id
      const copiedFileId = await StaticFile.idFromPrehash(preHash)

      const copyFile = StaticFile.blank(copiedFileId)
      for (const propertyId of [
        PropertyId.FILE_SIZE,
        PropertyId.FILE_NAME,
        PropertyId.FOLDER_USAGE_SIZE,
        PropertyId.FILE_MIME_TYPE,
      ]) {
        const property = sourceFile.property(propertyId)
        if (!isDefined(property))
          continue
        copyFile.addProperty(clone(property))
      }
      copyFile.addProperty({
        id: copiedFileId,
        propertyId: PropertyId.FILE_COPY_ORIGIN_ID,
        timestamp,
        author: myself.value.user.id,
        version: 0,
        status: PropertyStatus.StatusPending,
        schema: PropertySchema.FileIdValue,
        value: { type: PropertySchema.FileIdValue, fileId: originalFileId },
      })

      // set all the file id fields in the properties to the new file id
      copyFile.setId(copiedFileId)

      this.state.value.files[copyFile.id] = {
        fileId: copyFile.id,
        name: copyFile.name() ?? "",
        totalBytes: copyFile.size() ?? 0,
        completedBytes: 0,
        isFolder: copyFile.isFolder(),
        status: "prepared",
      }

      requests.push({
        requestId,
        offline: false,
        preHash,
        file: copyFile.inner(),
      })
    }

    const bytes = createFileCopyRequestDatagram(requests)
    cacheWorker().copyItems(
      this.state.value.transferId,
      this.state.value.folderId,
      myself.value.user.id,
      comlink.transfer(bytes, [bytes.buffer]), // makes the arraybuffers zero-copy
    )
  }
}

export class DesktopCopyJob extends CopyJob {
  async startCopy() {
    const { state: myself } = useSelf()
    const { copyFiles } = useTauri()
    if (!myself.value.user)
      return console.error("Cannot upload while logged out")

    const requestId = generateWorkerRequestId()
    const timestamp = uuidv7()
    const requests: FileCopyRequestDatagram[] = []
    for (const sourceFile of Object.values(this.state.value.sources)) {
      const preHash = StaticFile.clientPreHashId()
      const originalFileId = sourceFile.id
      const id = await StaticFile.idFromPrehash(preHash)
      const mimeTypeProperty = sourceFile.property(PropertyId.FILE_MIME_TYPE)
      if (!isDefined(mimeTypeProperty))
        throw new Error("File does not have a mime type")
      const copyFile = StaticFile.blank(id)
      copyFile.addProperty(mimeTypeProperty)
      for (const propertyId of [
        PropertyId.FILE_SIZE,
        PropertyId.FILE_NAME,
        PropertyId.FOLDER_USAGE_SIZE,
        PropertyId.FILE_MIME_TYPE,
      ]) {
        const property = sourceFile.property(propertyId)
        if (!isDefined(property))
          continue
        copyFile.addProperty(clone(property))
      }
      copyFile.addProperty({
        id,
        propertyId: PropertyId.FILE_COPY_ORIGIN_ID,
        timestamp,
        author: myself.value.user.id,
        version: 0,
        status: PropertyStatus.StatusPending,
        schema: PropertySchema.FileIdValue,
        value: { type: PropertySchema.FileIdValue, fileId: originalFileId },
      })
      requests.push({
        requestId,
        offline: false,
        preHash,
        file: copyFile.inner(),
      })
    }

    const bytes = createFileCopyRequestDatagram(requests)
    await copyFiles({
      transferId: this.state.value.transferId,
      userId: myself.value.user.id,
      folderId: this.state.value.folderId,
      bytes,
    })
    console.debug("Copy complete")
  }
}
